import { useEffect, useRef, type RefObject } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { useCreditsWidgetState } from '../../../lib/use-credits-widget-state';
import { useDailyCreditsResetLine, widgetStateResetInputs } from '../../../lib/use-daily-credits-reset-line';
import { creditsWidgetCopyKeys, CreditsWidgetIcon, CreditsWidgetCtaButton } from './CreditsWidgetShared';

/**
 * The in-chat low/out-of-credits widget (Figma 16346-95980) and its condensed sticky bar
 * (16346-96070). Two pieces sharing one state:
 *
 *  - `CreditsChatInlineCard` sits at the TOP of the transcript, in the scroll flow. An
 *    IntersectionObserver reports whether it is on screen.
 *  - `CreditsChatStickyBar` pins above the composer and shows whenever the state applies AND the inline
 *    card is NOT on screen — so on opening a chat (landed at the newest turn, the card scrolled off the
 *    top) or after scrolling down past it, the condensed bar stays put above the input. It follows the
 *    reader across conversations because it is driven by the CURRENT credit state, not the thread — a
 *    historical chat viewed when credits are healthy shows neither piece.
 *
 * Both render null when the state is `none` (fine / gated off), so a stale mount can't draw an empty
 * card or bar.
 */

/** The in-transcript card: icon + title, body, and a leading-aligned CTA. Reports its own on-screen
 *  visibility so the sticky bar can take over when it scrolls away.
 *
 *  `scrollRootRef` is the transcript scroll container and `rootTopInset` its top padding (the header
 *  clearance): the header OVERLAYS the top of the scroll box (the box is padded, not shortened), so a
 *  card scrolled up behind the header is still inside the box. Observing against the container with a
 *  top `rootMargin` of that inset makes "behind the header" read as off-screen, so the sticky appears —
 *  which a default viewport observer misses (the mobile-web bug). The container also clips the bottom,
 *  so a card scrolled below the composer already reads off-screen with no inset needed. */
export function CreditsChatInlineCard({
  onVisibilityChange,
  scrollRootRef,
  rootTopInset = 0,
}: {
  onVisibilityChange?: (visible: boolean) => void;
  scrollRootRef?: RefObject<Element | null>;
  rootTopInset?: number;
}) {
  const { t } = useTranslation();
  const state = useCreditsWidgetState();
  const resetLine = useDailyCreditsResetLine(widgetStateResetInputs(state));
  const ref = useRef<HTMLDivElement | null>(null);
  const active = state.level !== 'none';

  useEffect(() => {
    if (!active) {
      onVisibilityChange?.(false);
      return;
    }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      // No element / no observer support: treat as visible so the sticky bar (the fallback affordance)
      // does not double up with a card that is on screen.
      onVisibilityChange?.(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => onVisibilityChange?.(entry.isIntersecting), {
      root: scrollRootRef?.current ?? null,
      rootMargin: `-${rootTopInset}px 0px 0px 0px`,
      threshold: 0,
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [active, onVisibilityChange, scrollRootRef, rootTopInset]);

  if (!active) return null;
  const { titleKey, bodyKey, params } = creditsWidgetCopyKeys(state);

  return (
    <div
      ref={ref}
      // Figma 16346-95980: Surface/Soft fill, radius 16, p-20, gap-16, NO border.
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 16,
        padding: 20,
        borderRadius: 16,
        marginBottom: 16,
        background: 'var(--ds-surface-light-gray)', // Surface/Soft #eae9e9 (dark-safe)
        color: 'var(--ds-text-primary)',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <CreditsWidgetIcon />
          <span style={{ fontSize: '1rem', fontWeight: 'var(--sym-weight-semibold)', lineHeight: '1.25rem', letterSpacing: '-0.04em', color: 'var(--ds-text-primary)' }}>
            {t(titleKey, params)}
          </span>
        </span>
        {/* Daily OUT only, when the reset instant is known and ahead — see `lib/daily-credits-reset.ts`. */}
        {resetLine && (
          <span style={{ fontSize: '0.875rem', fontWeight: 'var(--sym-weight-medium)', lineHeight: '1.25rem', letterSpacing: '-0.02em', color: 'var(--ds-text-primary)' }}>
            {resetLine}
          </span>
        )}
        <span style={{ fontSize: '0.875rem', fontWeight: 400, lineHeight: '1.25rem', letterSpacing: '-0.02em', color: 'var(--ds-text-primary)' }}>
          {t(bodyKey, params)}
        </span>
      </div>
      <CreditsWidgetCtaButton />
    </div>
  );
}

/**
 * The condensed bar pinned above the composer (Figma 16346-96070): a Surface/Soft pill — coins + title
 * on the leading side, a tiny CTA on the trailing side — that TUCKS behind the composer (a negative
 * bottom margin, so the composer's rounded top overlaps its lower edge and it reads as a ledge peeking
 * out from behind the input). Shown by the panel when the inline card is off screen.
 */
export function CreditsChatStickyBar() {
  const { t } = useTranslation();
  const state = useCreditsWidgetState();
  const resetLine = useDailyCreditsResetLine(widgetStateResetInputs(state));
  if (state.level === 'none') return null;
  const { titleKey, params } = creditsWidgetCopyKeys(state);

  return (
    <div
      role="status"
      style={{
        display: 'flex',
        flexDirection: 'column',
        // Content at the top; the extra bottom padding is the ledge that tucks behind the composer.
        padding: '12px 12px 40px',
        borderRadius: 20, // Figma --spacing/20
        background: 'var(--ds-surface-light-gray)', // Surface/Soft #eae9e9 (dark-safe)
        border: '1px solid var(--ds-separator)', // Border/light/Default #dbdbdb (dark-safe)
        color: 'var(--ds-text-primary)',
        // Tuck behind the composer so its rounded top overlaps the pill's bottom ledge (Figma: 26px of
        // overlap, content-to-input ≈15px). The composer sits in a flex column with an 8px `gap` above
        // it, which would otherwise widen that separation — so tuck 8px MORE (−34, not the raw Figma
        // −26) to absorb the gap and keep the content-to-input distance at the Figma spacing.
        marginBottom: -34,
        position: 'relative',
        zIndex: 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, width: '100%' }}>
        <span style={{ display: 'flex', alignItems: 'flex-start', gap: 4, minWidth: 0, flex: 1 }}>
          <span style={{ display: 'inline-flex', flexShrink: 0, marginTop: 1 }}>
            <CreditsWidgetIcon />
          </span>
          {/* Wrap to a second line rather than ellipsise — a truncated "…on your Pro plan" loses the
              plan name, so the title is allowed up to two rows here (product decision). */}
          <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 'var(--sym-weight-semibold)', lineHeight: '1rem', letterSpacing: '-0.02em', minWidth: 0, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
              {t(titleKey, params)}
            </span>
            {resetLine && (
              <span style={{ fontSize: '0.75rem', fontWeight: 400, lineHeight: '1rem', letterSpacing: '-0.02em', minWidth: 0, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                {resetLine}
              </span>
            )}
          </span>
        </span>
        <span style={{ flexShrink: 0 }}>
          <CreditsWidgetCtaButton size="tiny" />
        </span>
      </div>
    </div>
  );
}
